import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down-medium.svg";
import { ReactComponent as IconChevronUp } from "@/assets/svgs/chevron-up-medium.svg";
import { ReactComponent as IconChevron } from '@/assets/svgs/profile-chevron.svg'
import { useEffect, useState } from "react";
import DropdownDS from "@/componentsDS/Dropdown/Dropdown";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import moment from "moment";
import api from "@/services/api/etr";
import { useFeature } from "@/hooks/use-feature/index";
import { renderUserEmailInline } from '@/utils/formatUserEmail';
import { featureConstant } from "@/constants/configuration/feature";

const CellModelTermsComponent = ({
	isReadOnly,
	schema,
	updatePayload,
	selectedModelTerms,
	setSelectedModelTerms,
}) => {
	const isSoftDeleteUserEnabled = useFeature(featureConstant.SOFT_DELETE_USER);
	const [modelTermsOptions, setModelTermsOptions] = useState([]);
	const [openSections, setOpenSections] = useState({});
	const [selectModelTerms, setSelectModelTerms] = useState(null);
	const [isDropdownDisabled, setIsDropdownDisabled] = useState(false);

	const {
		enum: enumOptions,
		fields_to_populate,
		url,
		key,
		value,
	} = schema["x-uioptions"];
	const { properties } = schema;
	const profile = useSelector((state) => state.auth.profile);

	useEffect(async () => {
		const fetchConfig = {
			internal: {
				fetchFn: api.fetchOrgTermClause,
				getUrl: () => url.replace(/:[^\\/]+/g, profile?.organizationId || ""),
			},
			external: {
				fetchFn: api.fetchOrgTermClauseExternal,
				getUrl: () => url,
			},
		};
		const config = fetchConfig[enumOptions];
		const [_, data] = await config.fetchFn(config.getUrl());

		const dropdownOptions = data?.terms?.map((item, index) => ({
			id: item?.[key],
			label: item?.[value],
			index,
			original: item,
		}));
		setModelTermsOptions(dropdownOptions);
	}, []);

	useEffect(() => {
		if (modelTermsOptions?.length === 1) {
			const onlyOption = modelTermsOptions[0];
			setIsDropdownDisabled(true);
			handleDropdownSelect(onlyOption.original.id);
		} else if (selectedModelTerms && modelTermsOptions.length > 0) {
			handleDropdownSelect(selectedModelTerms);
		}
	}, [selectedModelTerms, modelTermsOptions]);

	const handleDropdownSelect = (id) => {
		const value = modelTermsOptions?.find((modelTerm) => modelTerm.id === id);
		setSelectModelTerms(value?.original);
		handleSelectedModelTerms(value?.original);
		setSelectedModelTerms(id);
	};

	function mapFieldsToPayload(source) {
		const result = {};
		for (const [targetKey, path] of Object.entries(fields_to_populate)) {
			const value = path.split(".").reduce((obj, key) => obj?.[key], source);
			result[targetKey] = value || "";
		}
		return result;
	}

	const handleSelectedModelTerms = (selectedValue) => {
		const payload = mapFieldsToPayload(selectedValue);
		updatePayload({ ...payload });

		const newOpenSections = {};
		Object.keys(fields_to_populate).forEach((key) => {
			newOpenSections[`${selectedValue.id}_${key}`] = true;
		});
		setOpenSections(newOpenSections);
	};

	const toggleSection = (key) => {
		setOpenSections((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const updatedHTMLText = (item, value) => {
		return value.split(".").reduce((acc, part) => acc?.[part], item);
	};

	return (
		<>
			<div className="etr-igpni__model-terms__dropdown">
				<DropdownDS
					options={modelTermsOptions}
					onAdd={(value) => { handleDropdownSelect(value[0]?.id) }}
					value={[{id: selectedModelTerms}]}
					disabled={
						isReadOnly ||
						isDropdownDisabled ||
						modelTermsOptions?.length === 0
					}
					trigger={
						<div className={`dropdown-input__model-term-list__action ${isDropdownDisabled ? "dropdown--disabled" : "dropdown-input__model-term-list__action--isDisabled"}`}>
							{selectModelTerms?.[value] ? <span className='trim'>{selectModelTerms?.[value]}</span> : 
								<span className={`dropdown__placeholder ${isDropdownDisabled ? "dropdown__placeholder--disabled" : ""}`}>{"Select List of Templates"}</span>}
							<div className="dropdown__select__icon">
								<IconChevron />
							</div>
						</div>
					}
				/>
			</div>

			{selectModelTerms && (
				<div>
					{Object.entries(fields_to_populate).map(([key, value]) => {
						const sectionKey = `${selectModelTerms.id}_${key}`;
						const isOpen = openSections[sectionKey];
						return (
							properties?.[key]?.["x-uitype"] === "banner" && (
								<div
									className="etr-igpni__cell-model-terms"
									key={key}
								>
									<div className="etr-igpni__cell-model-terms__header">
										<div className="etr-igpni__cell-model-terms__header-left">
											<div className="etr-igpni__cell-model-terms__header-title">
												{properties?.[key]?.title ||
													key}
											</div>
											<div className="etr-igpni__cell-model-terms__header-date">
												Updated by{" "}
												{isSoftDeleteUserEnabled ? renderUserEmailInline(selectModelTerms?.modifiedBy) : selectModelTerms?.modifiedBy} on{" "}
												{moment(
													selectModelTerms.modifiedAt
												).format("DD MMM YYYY")}
											</div>
										</div>
										<div
											className="etr-igpni__cell-model-terms__header-right"
											onClick={() =>
												toggleSection(sectionKey)
											}
											role="none"
										>
											{isOpen ? (
												<span>
													{" "}
													Hide Details{" "}
													<IconChevronUp />{" "}
												</span>
											) : (
												<span>
													{" "}
													More Details{" "}
													<IconChevronDown />{" "}
												</span>
											)}
										</div>
									</div>
									{isOpen && (
										<div
											className="etr-igpni__cell-model-terms__footer"
											dangerouslySetInnerHTML={{
												__html: updatedHTMLText(
													selectModelTerms,
													value
												),
											}}
										/>
									)}
								</div>
							)
						);
					})}
				</div>
			)}
		</>
	);
};

CellModelTermsComponent.propTypes = {
	isReadOnly: PropTypes.any,
	schema: PropTypes.any,
	updatePayload: PropTypes.any,
	selectedModelTerms: PropTypes.string,
	setSelectedModelTerms: PropTypes.any,
};

export default CellModelTermsComponent;
